import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatPrice } from '../../../../../../common/global/helpers';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import PopoverDialog from '../../../../../../common/widgets/PopoverDialog';
import { config } from '../../../../config';
import { CustomerBudgetContext } from '../../../../contexts/CustomerBudgetContext';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { resources } from '../../../../resources/resources';
import WalletNav from '../WalletNav';
import WalletSkeleton from '../WalletSkeleton';

interface WalletProps {
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
}

interface WalletBoxContentProps {
  balance?: number | null;
  isLoading: boolean;
  label: string;
}

function WalletBoxContent({
  balance,
  isLoading,
  label,
}: Readonly<WalletBoxContentProps>) {
  const [integerPart, decimalPart] = useMemo(() => {
    if (balance == null) {
      return ['', undefined];
    }

    return formatPrice(balance).split('.');
  }, [balance]);

  return (
    <div className="wallet-box-content">
      <span className="wallet-box-marker">
        <Icons.Wallet />
      </span>

      <div className="wallet-box-info">
        <span className="wallet-box-label">{label}</span>

        <Loader
          loading={isLoading}
          loader={
            <span className="wallet-box-balance wallet-box-balance-loading">
              <WalletSkeleton />
            </span>
          }
        >
          <span className="wallet-box-balance">
            {integerPart}
            {decimalPart && <sup>.{decimalPart}</sup>}
            <span className="wallet-box-currency">{config.currency}</span>
          </span>
        </Loader>
      </div>
    </div>
  );
}

export default function WalletBox({
  isVisible,
  setIsVisible,
}: Readonly<WalletProps>) {
  const { t: translate } = useTranslation();
  const { isTablet } = useBreakpoints();
  const { customerInfo } = useContext(CustomerInfoContext);
  const { customerBudget, isCustomerBalanceLoading } = useContext(
    CustomerBudgetContext,
  );

  const balance = customerBudget?.benefitBudgetBalance;
  const isBalanceLoading = isCustomerBalanceLoading || balance == null;

  const closeWallet = () => setIsVisible(false);

  useEffect(() => {
    const htmlElement = document.documentElement;

    if (!isTablet) {
      htmlElement.classList.remove(
        'wallet-visible-details',
        'wallet-hidden-details',
      );
      setIsVisible(false);
      return;
    }

    htmlElement.classList.toggle('wallet-visible-details', isVisible);
    htmlElement.classList.toggle('wallet-hidden-details', !isVisible);

    return () => {
      htmlElement.classList.remove(
        'wallet-visible-details',
        'wallet-hidden-details',
      );
    };
  }, [isTablet, isVisible, setIsVisible]);

  if (!customerInfo.showWallet) {
    return null;
  }

  const walletBoxContent = (
    <WalletBoxContent
      balance={balance}
      isLoading={isBalanceLoading}
      label={translate(resources.benefitBalance)}
    />
  );

  return (
    <div className="wallet-box">
      {!isTablet ? (
        <PopoverDialog
          className="wallet-menu"
          placement="bottom"
          closeFromContent
          type="hover"
          content={<WalletNav />}
        >
          <Link className="btn" to="/my-account/dashboard">
            {walletBoxContent}
          </Link>
        </PopoverDialog>
      ) : (
        <>
          <button
            type="button"
            className="btn"
            onClick={() => setIsVisible(true)}
          >
            {walletBoxContent}
          </button>

          <div className="wallet-menu-mb">
            <WalletNav toggleExpand={closeWallet} />
          </div>
        </>
      )}
    </div>
  );
}
